/**
 * PremiumLayerToggleRow — a LayerToggleRow gated behind a supporter feature.
 *
 * When the user has the feature it renders a normal, working toggle. When they
 * don't, it renders a locked row (🔒 "Supporters" badge) that opens the
 * SupporterModal on tap. Keeps supporter logic out of the giant App.tsx menu
 * and guarantees the locked rows look identical to the real ones.
 */

import { ReactNode, useState } from 'react';
import { useSupporter } from '../contexts/SupporterContext';
import { useAuth } from '../contexts/AuthContext';
import { SupporterFeatures } from '../api/client';
import LayerToggleRow from './LayerToggleRow';
import SupporterModal from './SupporterModal';

interface PremiumLayerToggleRowProps {
  /**
   * The specific supporter entitlement this row requires. Omit to gate on
   * general supporter status (any active supporter) — used for features that
   * don't have their own SupporterFeatures flag (e.g. aircraft tracking).
   */
  feature?: keyof SupporterFeatures;
  label: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
  icon?: ReactNode;
  /** layerKey for the shared ⓘ info dialog. */
  infoKey?: string;
}

export default function PremiumLayerToggleRow({
  feature,
  label,
  checked,
  onToggle,
  icon,
  infoKey,
}: PremiumLayerToggleRowProps) {
  const { hasFeature, isSupporter } = useSupporter();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const [showModal, setShowModal] = useState(false);

  // Admins always preview; otherwise a specific feature check, or general
  // supporter status when no feature is named.
  const unlocked = isAdmin || (feature ? hasFeature(feature) : isSupporter);

  if (unlocked) {
    return (
      <LayerToggleRow
        label={label}
        checked={checked}
        onToggle={onToggle}
        icon={icon}
        infoKey={infoKey}
      />
    );
  }

  return (
    <>
      <LayerToggleRow
        label={label}
        checked={false}
        onToggle={() => setShowModal(true)}
        icon={icon}
        infoKey={infoKey}
        locked
      />
      {showModal && <SupporterModal onClose={() => setShowModal(false)} source={`layer:${feature ?? label}`} />}
    </>
  );
}
